import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Link2, ExternalLink, Satellite, Download, Copy } from "lucide-react";
import { copy } from "@/lib/clipboard";
import { FalsePositiveCard } from "@/components/FalsePositiveCard";
import { SkippedCalls } from "@/components/SkippedCalls";
import { api } from "@/lib/api";
import { SiteFooter } from "@/components/BrandFooter";
import { HAZARD_BASE, HAZARD_LABEL } from "@/lib/hazard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => (iso ? new Date(iso).toUTCString().slice(5, 22) + " UTC" : "—");

const hrs = (h) => (h == null ? "—" : `${Number.isInteger(h) ? h : Number(h).toFixed(1).replace(/\.0$/, "")}h`);
const BTN = "flex items-center gap-1.5 px-3 h-8 border border-white/10 text-[10px] font-mono uppercase tracking-wider text-atlas-secondary hover:text-white hover:border-white/30 transition-colors";

const Summary = ({ s }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="first-spotted-summary">
    {[
      ["Confirmed calls", s.confirmed, "text-white", "fs-confirmed"],
      ["Median lead vs agencies", hrs(s.median_lead_agency_h), "text-atlas-cyan", "fs-lead-agency"],
      ["Median lead vs news", hrs(s.median_lead_news_h), "text-emerald-400", "fs-lead-news"],
      ["Best lead", hrs(s.best_lead_h), "text-white", "fs-best"],
    ].map(([k, v, c, t]) => (
      <div key={k} className="border border-white/10 bg-atlas-surface/70 px-4 py-3">
        <div className="text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/85">{k}</div>
        <div className={`text-2xl font-mono ${c}`} data-testid={t}>{v}</div>
      </div>
    ))}
  </div>
);

const Fact = ({ k, v, testid }) => (
  <div className="min-w-0">
    <div className="text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/80">{k}</div>
    <div className="text-[12px] font-mono text-white truncate" data-testid={testid}>{v}</div>
  </div>
);

const ConfirmedCard = ({ b, i }) => {
  const p = b.proof || {};
  const share = () => copy(`${window.location.origin}/confirmed#${b.uid}`, "Share link copied");
  const copyCaption = () => copy(b.caption || "", "Caption copied");
  const card = (size) => `${BACKEND}/api/confirmed/${b.uid}/card?size=${size}&v=${b.pair_upgraded_at || ""}`;
  return (
    <article
      id={b.uid}
      className="border border-white/10 bg-atlas-surface/80 atlas-rise"
      style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}
      data-testid={`confirmed-item-${b.uid}`}
    >
      <div className="aspect-[2/1] bg-atlas-void flex items-center justify-center overflow-hidden">
        {b.image_path ? (
          <img src={`${BACKEND}${b.image_endpoint}?v=${b.pair_upgraded_at || ""}`} alt={`Before vs now — ${b.region}`} className="w-full h-full object-contain" />
        ) : (
          <span className="text-[10px] font-mono uppercase tracking-widest text-atlas-secondary/75">No clear satellite pair</span>
        )}
      </div>
      <div className="p-5 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: HAZARD_BASE[b.hazard] || "#00F0FF" }} />
              <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">
                Confirmed · {HAZARD_LABEL[b.hazard] || b.hazard}
              </span>
            </div>
            <h2 className="text-lg font-display text-white truncate">{b.region}</h2>
            <p className="text-[12px] text-atlas-secondary mt-1">{p.title}</p>
          </div>
          <div className="flex gap-5 shrink-0 text-right">
            <div data-testid={`confirmed-lead-${b.uid}`}>
              <div className="text-2xl font-mono text-atlas-cyan">{hrs(b.lead_hours)}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/80">ahead of {p.source || "agency"}</div>
            </div>
            <div data-testid={`confirmed-news-lead-${b.uid}`}>
              <div className="text-2xl font-mono text-emerald-400">{b.news_lead_hours != null ? hrs(b.news_lead_hours) : "—"}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/80">
                {b.news_lead_hours != null ? "ahead of the news" : "No news coverage found"}
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-white/10 pt-3">
          <Fact k="Vigilo Earth flagged" v={fmt(b.flagged_at)} />
          <Fact k="Event reported" v={fmt(p.observed_at)} />
          <Fact k="Confirmed by" v={`${p.source || "agency"}${p.external_id ? ` · ${p.external_id}` : ""}${p.basin ? ` · ${p.basin}` : ""}`} testid={`confirmed-source-${b.uid}`} />
          <Fact k="Imagery" v={b.image_credit || "—"} />
        </div>
        <div className="flex flex-wrap gap-2">
          {p.link && (
            <a href={p.link} target="_blank" rel="noopener noreferrer" data-testid={`confirmed-agency-link-${b.uid}`}
              className="flex items-center gap-1.5 px-3 h-8 border border-white/10 text-[10px] font-mono uppercase tracking-wider text-atlas-secondary hover:text-white transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> Agency record
            </a>
          )}
          <a href={card("square")} download data-testid={`confirmed-card-square-${b.uid}`} className={BTN}>
            <Download className="w-3.5 h-3.5" /> Square 1080
          </a>
          <a href={card("landscape")} download data-testid={`confirmed-card-landscape-${b.uid}`} className={BTN}>
            <Download className="w-3.5 h-3.5" /> Landscape 1200×675
          </a>
          <button onClick={copyCaption} data-testid={`confirmed-copy-caption-${b.uid}`} className={BTN}>
            <Copy className="w-3.5 h-3.5" /> Copy caption
          </button>
          <button onClick={share} data-testid={`confirmed-share-${b.uid}`}
            className="flex items-center gap-1.5 px-3 h-8 border border-atlas-cyan/40 text-[10px] font-mono uppercase tracking-wider text-atlas-cyan hover:bg-atlas-cyan hover:text-atlas-void transition-colors">
            <Link2 className="w-3.5 h-3.5" /> Copy share link
          </button>
        </div>
      </div>
    </article>
  );
};

export default function ConfirmedFeed() {
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api.confirmed().then((r) => { setItems(r.items || []); setSummary(r.summary || null); }).catch(() => setItems([]));
  }, []);
  return (
    <div className="h-screen overflow-y-auto bg-atlas-void text-atlas-foreground grid-backdrop" data-testid="confirmed-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link to="/" data-testid="confirmed-back" className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-atlas-secondary hover:text-white mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Observatory
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <BadgeCheck className="w-7 h-7 text-emerald-400" />
          <h1 className="text-4xl sm:text-5xl font-display text-white">Confirmed Events</h1>
        </div>
        <p className="text-sm text-atlas-secondary max-w-2xl mb-10">
          Every Vigilo Earth call later confirmed by a real agency event (GDACS, USGS, EMSC, NASA EONET, NOAA NHC, Copernicus EMS / GFM, NIFC, CWFIS), shown as a
          before-vs-now satellite pair. Only calls Vigilo Earth made before both the agency report and the first news
          report are listed. Latest first.
        </p>
        <FalsePositiveCard />
        <SkippedCalls />
        {summary && items?.length > 0 && <Summary s={summary} />}
        {items === null ? (
          <div className="text-[11px] font-mono text-atlas-secondary">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-white/15 p-10 text-center" data-testid="confirmed-empty">
            <Satellite className="w-6 h-6 text-atlas-cyan mx-auto mb-3" />
            <p className="text-sm text-white mb-1">No confirmed events yet.</p>
            <p className="text-[12px] text-atlas-secondary">
              Vigilo Earth publishes a pair here the moment one of its calls is verified against a real reported event.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-8" data-testid="confirmed-list">
            {items.map((b, i) => <ConfirmedCard key={b.uid} b={b} i={i} />)}
          </div>
        )}
        <SiteFooter />
      </div>
    </div>
  );
}
